import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  Sparkles, 
  Crown, 
  Clock, 
  Gift,
  ArrowRight,
  CheckCircle2,
  Loader2,
  PartyPopper,
  Heart,
  Shield,
  Zap,
  Users,
  Calendar,
  ShoppingBag,
  Star,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Full benefits list - implemented: true = available now, false = coming soon
const BETA_BENEFITS = [
  { icon: Heart, title: "Full Health Records", description: "Complete medical history, vaccinations, and health tracking for all your pets", implemented: true },
  { icon: Users, title: "Care Team Access", description: "Share pet info with vets, groomers, and family members", implemented: false },
  { icon: Sparkles, title: "Unlimited Pets", description: "Add as many pets as you want to your profile", implemented: true },
  { icon: Shield, title: "Ad-Free Experience", description: "Enjoy the platform without third-party advertisements", implemented: true },
  { icon: Zap, title: "Discount DNA Tests", description: "Get discounts on DNA testing and health monitoring services", implemented: false },
  { icon: Star, title: "Matchmaking & Ancestry", description: "Find relatives and potential matches for your pets", implemented: false },
  { icon: Calendar, title: "Advanced Calendar", description: "Schedule appointments, reminders, and track important dates", implemented: true },
  { icon: ShoppingBag, title: "Marketplace Access", description: "Buy and sell pet-related items in the community marketplace", implemented: true },
  { icon: Users, title: "Community Voting", description: "Vote on club decisions and help shape community policies", implemented: false },
  { icon: PartyPopper, title: "Beta Tester Recognition", description: "Special badge and recognition as an early supporter", implemented: true }
];


/**
 * Beta Membership Status Banner
 * Shows beta users their membership benefits and timeline
 * Collapsible with state persisted in localStorage
 */
const BetaMembershipBanner = ({ compact = false }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [showBenefits, setShowBenefits] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    return localStorage.getItem('betaBannerCollapsed') === 'true';
  });

  // Persist collapse state
  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('betaBannerCollapsed', String(newState));
  };

  useEffect(() => {
    if (user?.is_beta_user) {
      fetchStatus();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/membership/beta-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch beta status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/membership/activate-beta-membership`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Beta membership activated! Enjoy premium features at no cost!');
        fetchStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to activate');
      }
    } catch (err) {
      toast.error('Failed to activate beta membership');
    } finally {
      setActivating(false);
    }
  };

  // Don't show for non-beta users
  if (!user?.is_beta_user) return null;
  
  if (loading) {
    return (
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
        </CardContent>
      </Card>
    );
  }

  // Not yet activated
  if (!status?.beta_membership_active) {
    return (
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Gift className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">You're a Beta Tester!</h3>
              <p className="text-sm text-muted-foreground">
                Activate your membership benefits now
              </p>
            </div>
            <Button 
              onClick={handleActivate} 
              disabled={activating}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {activating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Activate <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate progress
  const now = new Date();
  const betaEnd = status.beta_ends_at ? new Date(status.beta_ends_at) : null;
  const graceEnd = status.beta_grace_period_ends_at ? new Date(status.beta_grace_period_ends_at) : null;
  
  let progressPercent = 0;
  let phase = 'beta';
  let daysRemaining = status.days_remaining_in_beta || 0;
  
  if (betaEnd && now >= betaEnd) {
    phase = 'grace';
    daysRemaining = status.days_remaining_in_grace || 0;
    progressPercent = 50 + ((180 - daysRemaining) / 180) * 50;
  } else if (betaEnd) {
    const totalBetaDays = 365;
    progressPercent = ((totalBetaDays - daysRemaining) / totalBetaDays) * 50;
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-100 to-orange-100 rounded-full">
        <Crown className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-medium text-amber-800">
          Beta Member
        </span>
        <Badge variant="secondary" className="text-xs">
          {daysRemaining} days left
        </Badge>
      </div>
    );
  }

  return (
    <>
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 overflow-hidden">
        <CardContent className="p-4">
          {/* Header Row - Title with collapse button in top-right */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Crown className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <span className="font-semibold text-lg text-amber-900 whitespace-nowrap">Beta Member</span>
            </div>
            <button 
              onClick={toggleCollapse}
              className="p-1.5 rounded-md hover:bg-amber-200/50 flex-shrink-0 transition-colors"
              aria-label={isCollapsed ? "Expand" : "Collapse"}
            >
              {isCollapsed ? (
                <ChevronDown className="w-5 h-5 text-amber-700" />
              ) : (
                <ChevronUp className="w-5 h-5 text-amber-700" />
              )}
            </button>
          </div>

          {/* Second Row - Days remaining + Beta Benefits button */}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-sm text-amber-700">
              <Clock className="w-4 h-4" />
              <span className="font-medium">{daysRemaining} days remaining</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              className="h-7 px-2"
              onClick={() => setShowBenefits(true)}
              data-testid="paid-benefits-btn"
            >
              <Badge className="bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Beta Benefits
              </Badge>
            </Button>
          </div>

          {/* Expanded Content */}
          {!isCollapsed && (
            <div className="mt-4 space-y-4">
              {/* Description */}
              <p className="text-sm text-muted-foreground">
                Enjoy all premium features at no cost during the beta period, and use the Beta Report to tell us anything - bugs, suggestions, features... we'll use them to improve!
              </p>

              {/* Timeline */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {phase === 'beta' ? 'Beta Period' : 'Grace Period'}
                  </span>
                </div>
                <Progress value={progressPercent} className="h-2" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Beta Start</span>
                  <span>Beta End</span>
                  <span>Grace End</span>
                </div>
              </div>

              {/* Quick Benefits Preview */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Full Health Records</span>
                </div>
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Care Team Access</span>
                </div>
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Unlimited Pets</span>
                </div>
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Ad-Free Experience</span>
                </div>
              </div>
              
              {/* See All Benefits Link */}
              <Button 
                variant="link" 
                className="p-0 h-auto text-amber-600 hover:text-amber-700"
                onClick={() => setShowBenefits(true)}
              >
                See all {BETA_BENEFITS.length} benefits <ArrowRight className="w-4 h-4 ml-1" />
              </Button>

              {/* Info */}
              <div className="bg-white/50 rounded-lg p-3 text-sm">
                <p className="text-muted-foreground">
                  <strong>After beta:</strong> You'll have 6 months to decide if you want to 
                  continue as a full member or switch to the free tier.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Benefits Dialog */}
      <Dialog open={showBenefits} onOpenChange={setShowBenefits}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-600" />
              Beta Membership Benefits
            </DialogTitle>
            <DialogDescription>
              All premium features included at no cost during the beta period
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {BETA_BENEFITS.map((benefit, idx) => {
              const IconComponent = benefit.icon;
              const isImplemented = benefit.implemented;
              return (
                <div 
                  key={idx} 
                  className={`flex items-start gap-3 p-3 rounded-lg border ${
                    isImplemented 
                      ? 'bg-green-50 border-green-100' 
                      : 'bg-amber-50 border-amber-100'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isImplemented ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    {isImplemented ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className={`font-medium ${isImplemented ? 'text-green-800' : 'text-amber-800'}`}>
                        {benefit.title}
                      </h4>
                      {!isImplemented && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-200 text-amber-700">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${isImplemented ? 'text-green-700' : 'text-amber-700'}`}>
                      {benefit.description}
                    </p>
                  </div>
                </div>
              );
            })}
            
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <h4 className="font-medium text-amber-800 mb-1">After Beta Period</h4>
              <p className="text-sm text-amber-700">
                When the beta ends, you'll have a 6-month grace period to decide:
              </p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1">
                <li>• <strong>Continue as Premium:</strong> Keep all benefits for a monthly fee</li>
                <li>• <strong>Switch to Free:</strong> Basic features at no cost</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BetaMembershipBanner;
